"use client";

import { forwardRef } from "react";
import type { ButtonProps, ButtonRef } from "./button.types";
import { buttonStyles } from "./button.styles";

function ButtonInner<T extends React.ElementType = "button">(props: ButtonProps<T>, ref: React.Ref<ButtonRef<T>>) {
  const {
    children,
    variant = "primary",
    context = "default",
    size = "m",
    elevation = "hard",
    tone,
    icon,
    className,
    as = "button" as T,
    disabled = false,
    ...rest
  } = props;

  const classes = buttonStyles({
    variant,
    size,
    elevation,
    disabled,
    context,
    tone,
    className,
  });

  const Component = as;

  const componentProps = {
    ...rest,
  };

  // Two-step cast required: componentProps is typed as a spread of generic
  // props, so a direct cast to Record<string, unknown> is rejected by TS.
  const mutableProps = componentProps as unknown as Record<string, unknown>;
  if (as === "button" || !as) {
    mutableProps.disabled = disabled;
  } else if (as === "a" && disabled) {
    mutableProps["aria-disabled"] = true;
    mutableProps.onClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
  }

  return (
    <Component ref={ref} className={classes} {...componentProps}>
      {children}
      {icon ?? null}
    </Component>
  );
}

// forwardRef is required for React 18 compatibility when Button is used as a
// Radix Slot / asChild child (e.g. DialogTrigger, PopoverTrigger). React 18
// does not support the React 19-style prop-based ref on plain function components.
// Object.assign folds in displayName without a second cast statement.
export const Button = Object.assign(
  forwardRef(ButtonInner) as <T extends React.ElementType = "button">(
    props: ButtonProps<T> & { ref?: React.Ref<ButtonRef<T>> },
  ) => React.ReactElement | null,
  { displayName: "Button" },
);
